import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { NewObjectService } from "../../lib/objects/service";
import { PromiseStatuses } from "../../lib/types";
import {
  FindAllUserResponse,
  UserDTO,
  UserRole
} from "./dto";
import { NewUsersService } from "./service";

interface UsersState {
  findClientsResponse?: FindAllUserResponse;
  findClientsStatus: PromiseStatuses;

  createFromProject?: { userRole: UserRole; userId?: string };

  findUsersAvatarsListResponse: { userId: string; objectId: string }[];
  findUsersAvatarsListStatus: PromiseStatuses;

  findUsersByUserIdResponse: UserDTO[]
  findUsersByUserIdStatus: PromiseStatuses
  findMeStatus: PromiseStatuses
  findMeResponse?: UserDTO
}

const initialState: UsersState = {
  findClientsStatus: "idle",
  findUsersAvatarsListResponse: [],
  findUsersAvatarsListStatus: "idle",
  findUsersByUserIdResponse: [],
  findUsersByUserIdStatus: 'idle',
  findMeStatus: 'idle'
};

export const getUsersByUserIds = createAsyncThunk(
  "users/getUsersByUserIds",
  async (
    userIds: string[],
  ): Promise<UserDTO[]> => {
    if (userIds.length === 0) {
      return Promise.resolve([])
    }
    let promises: Promise<UserDTO>[] = [];
    promises = userIds.map(async (id) => {
      const UsersService = NewUsersService();

      return UsersService.findUserByUserId(id);
    });
    return Promise.all(promises);
  }
);

export const findClients = createAsyncThunk(
  "users/findClients",
  async (_, thunkApi): Promise<FindAllUserResponse> => {
    const UsersService = NewUsersService();

    return UsersService.findAllUsers({
      role: UserRole.customer,
      order: true,
      sort: "name",
      //@ts-ignore
      itemsPerPage: 0,
      page: 0,
    });
  }
);

export const findUsersAvatarsList = createAsyncThunk(
  "users/findUsersAvatarsList",
  async (
    request: { objectId: string | null; userId: string }[],
    thunkApi
  ): Promise<{ userId: string; objectId: string }[]> => {
    let promises: Promise<{ userId: string; objectId: string }>[] = [];
    promises = request.map(async (user) => {
      if (user.objectId !== null && user.objectId !== undefined) {
        const objectService = NewObjectService();

        return objectService.findFileByIdWithUserId(user.objectId, user.userId);
      } else return Promise.resolve({ userId: user.userId, objectId: "" });
    });
    return Promise.all(promises);
  }
);

export const findMe = createAsyncThunk(
  "users/findMe",
  async (): Promise<UserDTO> => {
    const UsersService = NewUsersService();

    return UsersService.findMe();
  }
);

const usersSlice = createSlice({
  name: "users/slice",
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getUsersByUserIds.pending, (state) => {
        state.findUsersByUserIdStatus = "loading";
      })
      .addCase(getUsersByUserIds.fulfilled, (state, action) => {
        state.findUsersByUserIdStatus = "successfully";
        state.findUsersByUserIdResponse = action.payload;
      })
      .addCase(getUsersByUserIds.rejected, (state) => {
        state.findUsersByUserIdStatus = "failed";
      })
      .addCase(findClients.pending, (state) => {
        state.findClientsStatus = "loading";
      })
      .addCase(findClients.fulfilled, (state, action) => {
        state.findClientsStatus = "successfully";
        state.findClientsResponse = action.payload;
      })
      .addCase(findClients.rejected, (state) => {
        state.findClientsStatus = "failed";
      })
      .addCase(findUsersAvatarsList.pending, (state) => {
        state.findUsersAvatarsListStatus = "loading";
      })
      .addCase(findUsersAvatarsList.fulfilled, (state, action) => {
        state.findUsersAvatarsListStatus = "successfully";
        state.findUsersAvatarsListResponse = action.payload;
      })
      .addCase(findUsersAvatarsList.rejected, (state) => {
        state.findUsersAvatarsListStatus = "failed";
      })
      .addCase(findMe.pending, (state) => {
        state.findMeStatus = "loading";
      })
      .addCase(findMe.fulfilled, (state, action) => {
        state.findMeStatus = "successfully";
        state.findMeResponse = action.payload;
      })
      .addCase(findMe.rejected, (state) => {
        state.findMeStatus = "failed";
      });
  },
  reducers: {
    setFindUsersByUserIdResponse: (state, action) => {
      state.findUsersByUserIdResponse = action.payload;
    },
    setFindUsersByUserIdStatus: (state, action) => {
      state.findUsersByUserIdStatus = action.payload;
    },
    setCreateFromProject: (state, action: PayloadAction<{ userRole: UserRole; userId?: string } | undefined>) => {
      state.createFromProject = action.payload;
    },
  },
});

export const {
  setCreateFromProject,
  setFindUsersByUserIdResponse,
  setFindUsersByUserIdStatus,
} = usersSlice.actions;

export default usersSlice.reducer;
